import { useState, useRef, useEffect } from 'react';
import { useOpenClose } from 'core/hooks/useOpenClose';
import { BorderedContainer } from '../../../common/BorderedContainer';
import { SectionTitle } from '../../../common/SectionTitle';
import { IncidentDetailPatient } from 'sections/niigata/features/IncidentDetail/useIncidentDetailForNiigata';

type ListVideoProps = {
  /** 動画データ一覧 */
  videoList: IncidentDetailPatient['videoList'];
};

export const ListVideos = ({ videoList }: ListVideoProps) => {
  return (
    <BorderedContainer>
      <div className="flex flex-col gap-[24px]">
        <SectionTitle text="動画" />
        <div className="flex flex-wrap gap-[16px]">
          {videoList
            ?.filter(video => Boolean(video.preSignedUrl))
            .map(video => (
              <VideoCard key={video.id} src={video.preSignedUrl ?? ''} />
            ))}
        </div>
      </div>
    </BorderedContainer>
  );
};

type VideoCardProps = {
  /** 動画URL */
  src: string;
};

const VideoCard = ({ src }: VideoCardProps) => {
  const { isOpen, handleOpen, handleClose } = useOpenClose();
  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingThumbnail, setIsGeneratingThumbnail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const thumbnailVideoRef = useRef<HTMLVideoElement>(null);
  const thumbnailObjectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const safeSetState = (updater: () => void) => {
      if (!cancelled) {
        updater();
      }
    };

    // 初期化処理
    if (thumbnailObjectUrlRef.current) {
      URL.revokeObjectURL(thumbnailObjectUrlRef.current);
      thumbnailObjectUrlRef.current = null;
    }
    safeSetState(() => {
      setIsLoading(true);
      setIsGeneratingThumbnail(false);
      setError(null);
      setThumbnailUrl(null);
    });

    // サムネイル生成処理
    const video = thumbnailVideoRef.current;
    if (!video) {
      return () => {
        cancelled = true;
      };
    }

    const generateThumbnail = () => {
      // 読み込み完了 → サムネイル作成中に切り替え
      safeSetState(() => {
        setIsLoading(false);
        setIsGeneratingThumbnail(true);
      });
      try {
        // 動画の最初のフレーム（0.1秒）に移動
        video.currentTime = 0.1;
      } catch (err) {
        console.warn('Failed to set video currentTime:', err);
        safeSetState(() => {
          setIsGeneratingThumbnail(false);
        });
      }
    };

    const captureThumbnail = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          canvas.toBlob(blob => {
            if (!blob) {
              safeSetState(() => {
                setIsGeneratingThumbnail(false);
              });
              return;
            }
            const objectUrl = URL.createObjectURL(blob);
            const previousUrl = thumbnailObjectUrlRef.current;
            thumbnailObjectUrlRef.current = objectUrl;
            if (previousUrl) {
              URL.revokeObjectURL(previousUrl);
            }
            safeSetState(() => {
              setThumbnailUrl(objectUrl);
              setIsGeneratingThumbnail(false);
            });
          }, 'image/jpeg');
        }
      } catch (err) {
        // CORSエラー等でサムネイル生成に失敗した場合はプレースホルダーを表示
        console.warn('Failed to capture thumbnail:', err);
        safeSetState(() => {
          setIsGeneratingThumbnail(false);
        });
      }
    };

    // once: true で1回だけ発火させ、自動的にリスナーを削除
    video.addEventListener('loadeddata', generateThumbnail, { once: true });
    video.addEventListener('seeked', captureThumbnail, { once: true });

    // クリーンアップ（src変更時やアンマウント時にリスナーを確実に削除）
    return () => {
      cancelled = true;
      if (thumbnailObjectUrlRef.current) {
        URL.revokeObjectURL(thumbnailObjectUrlRef.current);
        thumbnailObjectUrlRef.current = null;
      }
      video.removeEventListener('loadeddata', generateThumbnail);
      video.removeEventListener('seeked', captureThumbnail);
    };
  }, [src]);

  const handleLoadedData = () => {
    setIsLoading(false);
    setError(null);
  };

  // モーダル内の動画読み込みエラー
  const handleError = () => {
    setIsLoading(false);
    setError('動画の読み込みに失敗しました\n\n画面を更新してください');
  };

  // サムネイル生成用動画のエラー（CORSなど）は無視してプレースホルダーを表示
  const handleThumbnailError = () => {
    setIsLoading(false);
    setIsGeneratingThumbnail(false);
    // サムネイル生成に失敗してもエラーとせず、プレースホルダーを表示
  };

  return (
    <>
      {/* サムネイル生成用の非表示動画 */}
      <video
        ref={thumbnailVideoRef}
        src={src}
        className="hidden"
        preload="metadata"
        crossOrigin="anonymous"
        onError={handleThumbnailError}
      />
      <button onClick={handleOpen} type="button">
        <div className="border border-outline rounded-[16px] overflow-hidden w-[152px] h-[152px] flex items-center justify-center bg-white relative">
          {/* サムネイル表示 */}
          {isLoading ? (
            <div className="text-textSecondary text-sm">読み込み中...</div>
          ) : isGeneratingThumbnail ? (
            <div className="text-textSecondary text-sm">
              サムネイル作成中...
            </div>
          ) : error ? (
            <div className="text-red-500 text-sm">{error}</div>
          ) : thumbnailUrl ? (
            <>
              <img
                src={thumbnailUrl}
                alt="動画サムネイル"
                className="object-cover w-full h-full"
              />
              <div className="absolute inset-0 bg-black bg-opacity-20" />
              <div className="absolute inset-0 flex items-center justify-center">
                <PlayIcon />
              </div>
            </>
          ) : (
            // サムネイル生成失敗時はプレースホルダーを表示
            <div className="flex flex-col items-center justify-center gap-2">
              <PlayIcon />
              <div className="text-textSecondary text-xs">動画</div>
            </div>
          )}
        </div>
      </button>

      {/* 動画モーダル */}
      {isOpen ? (
        <>
          <div
            className="fixed inset-0 bg-textPrimary bg-opacity-45 z-10"
            onClick={handleClose}
          />
          <div className="fixed inset-0 z-20 flex items-center justify-center p-[40px] pointer-events-none">
            <div className="flex flex-col gap-[16px] max-w-[90vw] max-h-[90vh] pointer-events-auto">
              <button onClick={handleClose} className="ml-auto" type="button">
                <CloseIcon />
              </button>
              <video
                ref={videoRef}
                src={src}
                controls
                className="max-w-full max-h-[calc(90vh-80px)] object-contain"
                playsInline
                onLoadedData={handleLoadedData}
                onError={handleError}
              >
                お使いのブラウザは動画タグをサポートしていません。
              </video>
            </div>
          </div>
        </>
      ) : (
        <></>
      )}
    </>
  );
};

const PlayIcon = () => {
  return (
    <div className="relative">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="white"
        className="w-16 h-16 drop-shadow-lg"
      >
        {/* 円 */}
        <circle cx="12" cy="12" r="10" fill="rgba(0, 0, 0, 0.5)" />
        {/* 三角（再生アイコン） */}
        <path d="M10 8v8l6-4z" fill="white" />
      </svg>
    </div>
  );
};

const CloseIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth="2"
      stroke="currentColor"
      className="size-10"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18 18 6M6 6l12 12"
        stroke="#FDFDFD"
      />
    </svg>
  );
};
